import {
  OPPORTUNITY_STAGES,
  OPPORTUNITY_STAGE_LABELS,
  REPLY_INTENTS,
  REPLY_INTENT_LABELS,
  TASK_PRIORITY_LABELS,
  type ReplyIntent,
  type EmailListItemDTO,
  type OpportunityDTO,
  type TaskDTO,
  type TimelineItemDTO,
  type TimelinePageDTO,
} from '@hearth/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Mail, Plus, StickyNote } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Checkbox, EmptyState, Spinner, useToast } from '../../../components/ui';
import { MoneyText, OwnerAvatar } from '../../../components/crm';
import { api, errorMessage } from '../../../lib/api';
import { formatDate, formatDateTime } from '../../../lib/format';
import { formatEntryTime } from '../../tasks/entryModel';
import { TaskRepeatIcon } from '../../tasks/TaskMeta';
import { invalidateTaskQueries } from '../../tasks/taskQueries';

export function TimelineTab({ businessId }: { businessId: string }) {
  const [types, setTypes] = useState<string[]>([]);
  const { data, isLoading } = useQuery({
    queryKey: ['leads', businessId, 'timeline', types],
    queryFn: ({ signal }) => api.get<TimelinePageDTO>(`/api/leads/${businessId}/timeline?limit=50${types.length > 0 ? `&types=${types.join(',')}` : ''}`, { signal }),
  });
  const groups: { label: string; items: TimelineItemDTO[] }[] = [];
  for (const item of data?.items ?? []) {
    const label = formatDate(item.occurredAt);
    const last = groups.at(-1);
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {(['email_sent', 'email_received', 'call', 'meeting', 'note_added', 'opportunity_stage_changed', 'stage_changed', 'relationship_changed', 'sequence_enrolled'] as const).map((type) => {
          const active = types.includes(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => setTypes(active ? types.filter((t) => t !== type) : [...types, type])}
              className={`rounded-full border px-2 py-0.5 text-xs ${active ? 'border-ember-300 bg-ember-50 text-ember-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              {type.replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
      {isLoading ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <EmptyState compact icon={StickyNote} title="Nothing has happened yet" description="Emails, calls, notes and stage changes appear here as you work the lead." />
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">{group.label}</p>
              <ol className="space-y-2 border-l border-slate-200 pl-4">
                {group.items.map((item) => (
                  <li key={item.id} className="relative">
                    <span className="absolute -left-[21px] top-1.5 size-2 rounded-full bg-slate-300" />
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-[13px] text-slate-800">{item.summary}</span>
                      <span className="text-xs text-slate-400">{formatDateTime(item.occurredAt)}</span>
                      {item.actor && <span className="text-xs text-slate-500">{item.actor.name}</span>}
                      {item.actorType === 'automation' && <Badge color="violet" size="sm">automated</Badge>}
                      {item.isMeaningful && <Badge color="green" size="sm">two-way</Badge>}
                      <ReplyIntentBadge item={item} />
                    </div>
                    {item.note && <p className="mt-1 whitespace-pre-wrap rounded bg-slate-50 p-2 text-[13px] text-slate-700">{item.note.body}</p>}
                    {item.body && !item.note && <p className="mt-1 whitespace-pre-wrap text-[13px] text-slate-600">{item.body}</p>}
                    {item.type !== 'email_received' && evidenceOf(item) && (
                      <p className="mt-1 border-l-2 border-slate-200 pl-2 text-xs italic text-slate-500">“{evidenceOf(item)}”</p>
                    )}
                    {item.email && (
                      <p className="mt-1 text-xs text-slate-500">
                        {item.email.subject} → {item.email.toEmail} ({item.email.status})
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const INTENT_COLORS: Record<ReplyIntent, string> = { interested: 'lime', not_interested: 'slate', unsubscribe: 'red', neutral: 'slate' };

/** The words in the lead's reply that decided its reading (metadata written with the automatic status change). */
function evidenceOf(item: TimelineItemDTO): string | null {
  const evidence = item.metadata.evidence;
  return typeof evidence === 'string' && evidence.trim() !== '' ? evidence : null;
}

/** How a lead's reply read (set automatically on the received email); plain replies get no badge. */
function ReplyIntentBadge({ item }: { item: TimelineItemDTO }) {
  const intent = item.metadata.intent as ReplyIntent;
  if (item.type !== 'email_received' || !REPLY_INTENTS.includes(intent) || intent === 'neutral') return null;
  const evidence = evidenceOf(item);
  return (
    <Badge color={INTENT_COLORS[intent]} size="sm" title={evidence ? `Because the reply says “${evidence}”` : undefined}>
      {REPLY_INTENT_LABELS[intent]}
    </Badge>
  );
}

export function DealsTab({ businessId, onNew }: { businessId: string; onNew: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['opportunities', 'list', { businessId }],
    queryFn: ({ signal }) => api.get<{ items: OpportunityDTO[] }>(`/api/opportunities?businessId=${businessId}`, { signal }),
  });
  const moveStage = async (opportunity: OpportunityDTO, stage: string) => {
    let lostReason: string | undefined;
    if (stage === 'lost') {
      const answer = window.prompt('Why was this deal lost?');
      if (!answer) return;
      lostReason = answer;
    }
    try {
      await api.post(`/api/opportunities/${opportunity.id}/stage`, { stage, lostReason });
      toast.success(`Moved to ${OPPORTUNITY_STAGE_LABELS[stage as keyof typeof OPPORTUNITY_STAGE_LABELS]}`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['opportunities'] }),
        queryClient.invalidateQueries({ queryKey: ['leads', businessId] }),
      ]);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  if (isLoading) return <Spinner />;
  if (!data || data.items.length === 0) {
    return (
      <EmptyState
        compact
        icon={Plus}
        title="No deals yet"
        description="Turn a conversation into a tracked opportunity so the value and the quote are on record."
        action={<Button variant="primary" icon={Plus} onClick={onNew}>New opportunity</Button>}
      />
    );
  }
  return (
    <div className="space-y-2">
      {data.items.map((opportunity) => (
        <div key={opportunity.id} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-[13px]">
          <span className="font-medium text-slate-900">{opportunity.title}</span>
          <Badge color={opportunity.stage === 'won' ? 'green' : opportunity.stage === 'lost' ? 'red' : 'amber'}>{OPPORTUNITY_STAGE_LABELS[opportunity.stage]}</Badge>
          <MoneyText amount={opportunity.estimatedValue} currency={opportunity.currency} />
          {opportunity.expectedCloseDate && <span className="text-slate-500">close {formatDate(opportunity.expectedCloseDate)}</span>}
          <span className="text-slate-500">{opportunity.daysInStage}d in stage</span>
          {opportunity.quoteNumber && <span className="text-slate-500">quote {opportunity.quoteNumber}</span>}
          <div className="ml-auto flex items-center gap-1.5">
            {OPPORTUNITY_STAGES.filter((stage) => stage !== opportunity.stage).map((stage) => (
              <Button key={stage} size="sm" variant="ghost" onClick={() => void moveStage(opportunity, stage)}>
                {OPPORTUNITY_STAGE_LABELS[stage]}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function TasksTab({ businessId, onNew, onEdit }: { businessId: string; onNew: () => void; onEdit: (task: TaskDTO) => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['tasks', 'list', { businessId }],
    queryFn: ({ signal }) => api.get<{ items: TaskDTO[] }>(`/api/tasks?businessId=${businessId}&status=open&scope=all`, { signal }),
  });
  const complete = async (task: TaskDTO) => {
    try {
      await api.post(`/api/tasks/${task.id}/complete`, {});
      await invalidateTaskQueries(queryClient, businessId);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  if (isLoading) return <Spinner />;
  if (!data || data.items.length === 0) {
    return <EmptyState compact icon={CalendarPlus} title="No open tasks" description="Schedule the next step so nothing slips." action={<Button icon={CalendarPlus} onClick={onNew}>Add a task</Button>} />;
  }
  return (
    <ul className="space-y-2">
      {data.items.map((task) => (
        <li key={task.id} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-[13px]">
          <Checkbox aria-label={`Complete ${task.title}`} checked={false} onChange={() => void complete(task)} />
          <button type="button" className="min-w-0 font-medium text-slate-900 hover:text-ember-700" onClick={() => onEdit(task)}>
            {task.title}
          </button>
          <TaskRepeatIcon task={task} />
          <Badge color={task.priority === 'urgent' || task.priority === 'high' ? 'amber' : 'slate'} size="sm">
            {TASK_PRIORITY_LABELS[task.priority]}
          </Badge>
          <span className="text-slate-500">{task.dueAt ? formatEntryTime(task) : 'no date'}</span>
          <OwnerAvatar userId={task.assignedTo} showName />
        </li>
      ))}
    </ul>
  );
}

export function EmailsTab({ businessId, onCompose }: { businessId: string; onCompose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['emails', 'list', { businessId }],
    queryFn: ({ signal }) => api.get<{ items: EmailListItemDTO[] }>(`/api/emails?businessId=${businessId}&pageSize=50`, { signal }),
  });
  if (isLoading) return <Spinner />;
  if (!data || data.items.length === 0) {
    return <EmptyState compact icon={Mail} title="No emails yet" description="Send an introduction or a quote follow-up." action={<Button icon={Mail} onClick={onCompose}>Compose</Button>} />;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {data.items.map((email) => (
        <li key={email.id} className="flex flex-wrap items-center gap-2 py-2 text-[13px]">
          <Badge color={email.direction === 'inbound' ? 'green' : email.status === 'failed' || email.status === 'bounced' ? 'red' : 'slate'} size="sm">
            {email.direction === 'inbound' ? 'reply' : email.status}
          </Badge>
          <span className="font-medium text-slate-900">{email.subject}</span>
          <span className="text-slate-500">{email.direction === 'inbound' ? `from ${email.fromEmail}` : `to ${email.toEmail}`}</span>
          {email.source !== 'manual' && <Badge color="violet" size="sm">{email.source}</Badge>}
          <span className="ml-auto text-slate-400">{formatDateTime(email.sentAt ?? email.receivedAt ?? email.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}
